// "Soluzioni compatibili" search, as the HTML: the first search starts from the button; afterwards every input change
// (and every change of the Preforo / Rondella options) clears the table and runs the search again after 800 ms. A
// search still running when the data change is cancelled and started again.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import useI18n from "../../../hooks/useTranslations";
import {
  findCompatibleSolutions,
  type CalculationInput,
  type EngineCatalog,
  type SolutionsProgress,
  type SolutionsResult,
} from "../../../http/screwConnector/formulas";

const REFRESH_DELAY_MS = 800;

type SolutionsSearchParams = {
  input: CalculationInput;
  catalog: EngineCatalog | null;
  manualInputMode: boolean;
};

export type SolutionsSearch = {
  predrilled: boolean;
  washer: boolean;
  setPredrilled: (checked: boolean) => void;
  setWasher: (checked: boolean) => void;
  running: boolean;
  progress: SolutionsProgress | null;
  result: SolutionsResult | null;
  search: () => void;
  // the data changed: clear the table (and search again after the first search)
  invalidate: () => void;
};

export function useSolutionsSearch({ input, catalog, manualInputMode }: SolutionsSearchParams): SolutionsSearch {
  const texts = useI18n().translation.screwConnectorModule.calculation;
  const [predrilled, setPredrilledState] = useState(false);
  const [washer, setWasherState] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<SolutionsProgress | null>(null);
  const [result, setResult] = useState<SolutionsResult | null>(null);

  const latest = useRef({ input, catalog, manualInputMode, predrilled, washer, texts });
  useLayoutEffect(() => {
    latest.current = { input, catalog, manualInputMode, predrilled, washer, texts };
  });
  const autoRefresh = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const timer = useRef(0);

  const cancelRunning = useCallback(() => {
    if (!controller.current) return;
    controller.current.abort();
    controller.current = null;
    setRunning(false);
    setProgress(null);
  }, []);

  const search = useCallback(async () => {
    const {
      input: data,
      catalog: cat,
      manualInputMode: manual,
      predrilled: pre,
      washer: rond,
      texts: txt,
    } = latest.current;
    if (!cat) return;
    autoRefresh.current = true;
    window.clearTimeout(timer.current);
    cancelRunning();
    const own = new AbortController();
    controller.current = own;
    setRunning(true);
    setProgress(null);
    setResult(null);
    try {
      const found = await findCompatibleSolutions(data, cat, {
        texts: txt,
        solPreforo: pre,
        solRondella: rond,
        signal: own.signal,
        onProgress: (p) => {
          if (!own.signal.aborted) setProgress(p);
        },
        manualInputMode: manual,
      });
      if (!own.signal.aborted) setResult(found);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) throw e;
    } finally {
      if (controller.current === own) {
        controller.current = null;
        setRunning(false);
        setProgress(null);
      }
    }
  }, [cancelRunning]);

  const invalidate = useCallback(() => {
    cancelRunning();
    setResult(null);
    window.clearTimeout(timer.current);
    if (autoRefresh.current) timer.current = window.setTimeout(() => void search(), REFRESH_DELAY_MS);
  }, [cancelRunning, search]);

  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
      controller.current?.abort();
    },
    [],
  );

  const setPredrilled = (checked: boolean) => {
    setPredrilledState(checked);
    invalidate();
  };
  const setWasher = (checked: boolean) => {
    setWasherState(checked);
    invalidate();
  };

  return {
    predrilled,
    washer,
    setPredrilled,
    setWasher,
    running,
    progress,
    result,
    search: () => void search(),
    invalidate,
  };
}
